import { useKeycloak } from "@react-keycloak/web";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useOutletContext } from "react-router-dom";

import {
  IconDownloadCloud01,
  IconTrash01,
  IconUserDown01,
  IconUserMinus02,
  IconUsers01,
} from "@pentabd/icons";
import { Avatar, Badge, Button, Dropdown, Header } from "@pentabd/ui";

import {
  ErrorModal,
  ExtraRoleModal,
  HeartBit,
  SuccessModal,
} from "@components";
import { ChangeStatusConfirmationModal } from "./ChangeStatusConfirmationModal";
import { PendingAccountModal } from "./PendingAccountModal";

import { useEmployeeDetailsWithRefetch } from "@hooks/HRM/employee/useEmployee";
import { useResignationList } from "@hooks/HRM/resignations/useResignationList";
import { useRetirementList } from "@hooks/HRM/retirements/useRetirementList";
import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";

import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";

import { downloadEmployeeFile } from "@api/downloadEmployeeFile";
import { changeAccountStatus, deleteExtraResponsibility } from "@api/employee";
import { OutletContextType } from "@type/layout";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

const { DRAFT, IN_PROGRESS, APPROVED, SUCCESS, FAILED } = STATUS;
const {
  CREATE,
  HRM: { EMPLOYEES, RETIREMENTS, RESIGNATION },
} = ROUTES;

export const EmployeePageHeader = ({
  id,
  isProfileDisabled,
}: {
  id: number;
  isProfileDisabled: boolean;
}) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [retirementOnGoing, setRetirementOnGoing] = useState(true);
  const [resignationOnGoing, setResignationOnGoing] = useState(true);
  const [isPendingAccountModalOpen, setIsPendingAccountModalOpen] =
    useState(false);
  const [isAccountStatusModalOpen, setIsAccountStatusModalOpen] =
    useState(false);
  const [deleteRoleModalOpen, setDeleteRoleModalOpen] =
    useState<boolean>(false);

  const closeDeleteRoleModal = () => {
    setDeleteRoleModalOpen(false);
  };

  const { keycloak } = useKeycloak();
  const token = keycloak.token as string;

  const { t } = useTranslation();
  const { user } = useUser();
  const { realm_roles: roles, employeeMasterId: loggedInUserId } = user || {};
  const { refetchHeaderState, onRefetchHeaderState } =
    useOutletContext<OutletContextType>();
  const { employeeInfo, handleQuery } = useEmployeeDetailsWithRefetch(id);
  const { data: retirementData } = useRetirementList(id);
  const { data: resignationData } = useResignationList(id);

  const isOnGoing = (data: any) =>
    data?.some(
      (item: any) =>
        item.employee.id === Number(id) &&
        [DRAFT, IN_PROGRESS, APPROVED].includes(item.status),
    );

  useEffect(() => {
    setRetirementOnGoing(isOnGoing(retirementData));
    setResignationOnGoing(isOnGoing(resignationData));
  }, [retirementData, resignationData]);

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closePendingAccountModal = () => setIsPendingAccountModalOpen(false);
  const closeAccountStatusModal = () => setIsAccountStatusModalOpen(false);

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    closeDeleteRoleModal();
    closePendingAccountModal();
    closeAccountStatusModal();
    onRefetchHeaderState();
  };

  useEffect(() => {
    handleQuery();
  }, [refetchHeaderState]);

  if (employeeInfo) {
    const {
      id: empMasterId,
      tempApprovedAccount,
      tempPendingAccount,
      isActive,
    } = employeeInfo;

    const { employeeName, employeePosition, employeeImage } =
      GetEmployeeDetails(employeeInfo);

    const handleAccountStatus = () => {
      changeAccountStatus(id, isActive, (res) => {
        if (res.status === SUCCESS) setIsSuccessModalOpen(true);
        else if (res.status === FAILED) setIsErrorModalOpen(true);
      });
    };

    const deleteExtraRole = (empID: number, serviceEndDate: string) => {
      deleteExtraResponsibility(empID, serviceEndDate, (res) => {
        if (res.status === SUCCESS) setIsSuccessModalOpen(true);
        else if (res.status === FAILED) setIsErrorModalOpen(true);
      });
    };

    const breadcrumbs = [
      {
        icon: <IconUsers01 fill="dark" size="20" />,
      },
      {
        label: t("EMPLOYEE.BREADCRUMBS.FIRST_STEP"),
        link: EMPLOYEES,
      },
      {
        label: employeeName,
      },
    ];

    const BtnLayout = ({
      children,
      path,
    }: {
      children: ReactNode;
      path: string;
    }) => (
      <Link to={`${path}/${id}/${CREATE}`}>
        <Button
          fill="fill"
          size="sm"
          type="default"
          htmlType="button"
          className="bg-primary-lightest text-primary-middark border border-primary-lighter"
        >
          {children}
        </Button>
      </Link>
    );

    const actionButtons = [
      {
        ...(getAccess(roles, "ACCOUNT_STATUS_UPDATE") && !isProfileDisabled ? (
          <Button
            fill="fill"
            size="sm"
            type={isActive ? "success" : "danger"}
            htmlType="button"
            onClick={() => setIsAccountStatusModalOpen(true)}
          >
            {t(
              `EMPLOYEE.ACTION_BUTTONS.${
                isActive ? "ACTIVE_ACCOUNT" : "INACTIVE_ACCOUNT"
              }`,
            )}
          </Button>
        ) : (
          <></>
        )),
      },
      {
        ...(roles?.includes("suspended_employee") && id === loggedInUserId ? (
          <Badge
            label="Suspended"
            type="danger"
            size="sm"
            className="text-nowrap w-max"
          />
        ) : (
          <></>
        )),
      },
      {
        ...(getAccess(roles, "RETIREMENT_INITIATE") &&
        !isProfileDisabled &&
        !retirementOnGoing ? (
          <BtnLayout path={RETIREMENTS}>
            <IconUserMinus02 size="20" fill="info" />
            {t("RETIREMENT_LIST.INITIATE_BUTTON")}
          </BtnLayout>
        ) : (
          <></>
        )),
      },
      {
        ...(getAccess(roles, "RESIGNATION_INITIATE") &&
        !isProfileDisabled &&
        !resignationOnGoing ? (
          <BtnLayout path={RESIGNATION}>
            <IconUserDown01 size="20" fill="info" />
            {t("RESIGNATIONS.INITIATE_BUTTON")}
          </BtnLayout>
        ) : (
          <></>
        )),
      },
      {
        ...(getAccess(roles, "CAN_ADD_EXTRA_RESPONSIBILITY") &&
        !isProfileDisabled &&
        (tempApprovedAccount?.length > 0 || tempPendingAccount?.length > 0) ? (
          <Button
            fill="fill"
            size="sm"
            type="warning"
            htmlType="button"
            onClick={() => setDeleteRoleModalOpen(true)}
          >
            <IconTrash01 size="20" fill="white" />
            {t("EMPLOYEE.ACTION_BUTTONS.DELETE_EXTRA_ACCOUNT")}
          </Button>
        ) : (
          <></>
        )),
      },
      {
        ...(empMasterId === Number(loggedInUserId) &&
        !isProfileDisabled &&
        tempPendingAccount?.length > 0 ? (
          <div
            className="d-flex align-items-center gap-4"
            onClick={() => setIsPendingAccountModalOpen(true)}
          >
            <HeartBit />
            <Button fill="outline" size="sm" type="info" htmlType="button">
              {t("EMPLOYEE.ACTION_BUTTONS.PENDING_ACCOUNT")}
            </Button>
          </div>
        ) : (
          <></>
        )),
      },
      <Dropdown
        buttonType="customIcon"
        customIcon={
          <div className="btn gap-4 btn-sm btn-info-fill">
            <IconDownloadCloud01 size="20" fill="white" />
            {t("EMPLOYEE.ACTION_BUTTONS.DOWNLOAD")}
          </div>
        }
        listItem={[
          {
            name: t("EMPLOYEE.ACTION_BUTTONS.LONG_PDS"),
            onClick: () =>
              downloadEmployeeFile({
                pdsType: "long",
                employeeId: id,
                employeeName,
                token,
              }),
          },
          {
            name: t("EMPLOYEE.ACTION_BUTTONS.SHORT_PDS"),
            onClick: () =>
              downloadEmployeeFile({
                pdsType: "short",
                employeeId: id,
                employeeName,
                token,
              }),
          },
        ]}
      />,
    ];

    return (
      <>
        <Header
          breadcrumbs={breadcrumbs}
          headerText={{
            header: employeeName,
            subHeader: employeePosition,
          }}
          avatar={<Avatar name={employeeName} imageURL={employeeImage} />}
          actions={actionButtons}
        />
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={closeSuccessModal}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
        <PendingAccountModal
          isModalOpen={isPendingAccountModalOpen}
          setIsPendingAccountModalOpen={setIsPendingAccountModalOpen}
          action={closePendingAccountModal}
          pendingData={tempPendingAccount || []}
          t={t}
          id={id}
        />
        <ExtraRoleModal
          isModalOpen={deleteRoleModalOpen}
          onCloseAction={closeDeleteRoleModal}
          roles={tempApprovedAccount || []}
          pendingRoles={tempPendingAccount || []}
          deleteAction={deleteExtraRole}
        />
        <ChangeStatusConfirmationModal
          isActive={isActive}
          isModalOpen={isAccountStatusModalOpen}
          onClose={closeAccountStatusModal}
          action={handleAccountStatus}
        />
      </>
    );
  } else return <></>;
};
